///
/// EarthBrowser
///
/// Created by Matt Giger on 9/10/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Post-checkout status card shown over the globe. Stripe redirects back to
/// the main page with ?checkout=success or ?checkout=cancelled; this renders
/// the outcome without leaving the app. Success polls the subscription query
/// until the webhook has recorded the new subscription.

import { Button } from '@/components/ui/button'
import { setSubscribeOpen } from '@/redux/dialogSlice'
import { useAppDispatch } from '@/redux/store'
import { useGetUserSubscriptionQuery } from '@/redux/userApi'
import { PartyPopper, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

// After this long without the webhook landing, stop implying it's imminent
// and tell the user their payment is safe + how to reach support
const ACTIVATION_TIMEOUT_MS = 30_000

export function CheckoutStatusOverlay() {
	const [searchParams, setSearchParams] = useSearchParams()
	const status = searchParams.get('checkout')

	if (status !== 'success' && status !== 'cancelled') {
		return null
	}

	const dismiss = () => {
		const params = new URLSearchParams(searchParams)
		params.delete('checkout')
		params.delete('session_id')
		setSearchParams(params, { replace: true })
	}

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[3px] animate-in fade-in duration-300">
			<div className="relative max-w-md w-full mx-4">
				<div className="bg-background/60 backdrop-blur-xl border border-border/50 rounded-3xl shadow-2xl overflow-hidden">
					{status === 'success'
						? <CheckoutSuccess onDismiss={dismiss} />
						: <CheckoutCancelled onDismiss={dismiss} />}
				</div>
			</div>
		</div>
	)
}

// The Stripe webhook that records the subscription can lag the redirect by a
// few seconds, so poll until the yearly subscription row appears. If it
// doesn't appear within the timeout, stop spinning: the payment went through
// (Stripe redirected here), so reassure rather than alarm.
function CheckoutSuccess({ onDismiss }: { onDismiss: () => void }) {
	const [timedOut, setTimedOut] = useState(false)
	const { data: subscriptions } = useGetUserSubscriptionQuery(undefined, {
		pollingInterval: timedOut ? 10_000 : 2000,
	})

	useEffect(() => {
		const timer = setTimeout(() => setTimedOut(true), ACTIVATION_TIMEOUT_MS)
		return () => clearTimeout(timer)
	}, [])

	const isActive = subscriptions?.some((subscription) =>
		subscription.subscription_id === 'yearly' &&
		(subscription.status === 'active' || subscription.status === 'trialing')
	) ?? false

	if (!isActive && timedOut) {
		return (
			<div className="flex flex-col items-center gap-4 px-6 py-6">
				<h3 className="text-base font-bold text-foreground">Payment received</h3>
				<p className="text-xs text-muted-foreground text-center">
					Activation is taking longer than expected. Your subscription will appear
					in your profile shortly — no need to purchase again. If it hasn&apos;t shown
					up within a few minutes, <Link to="/contact" className="underline text-foreground">contact support</Link> and
					we&apos;ll sort it out.
				</p>
				<Button variant="ghost" onClick={onDismiss} className="rounded-full text-muted-foreground">
					Continue exploring
				</Button>
			</div>
		)
	}

	return (
		<div className="flex flex-col items-center gap-4 px-6 py-6">
			{isActive ? (
				<>
					<div className="relative">
						<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-full blur-md opacity-75" />
						<div className="relative bg-black/40 backdrop-blur-md rounded-full p-3 border border-white/20">
							<PartyPopper className="h-6 w-6 text-cyan-400" />
						</div>
					</div>
					<h3 className="text-base font-bold text-foreground">You&apos;re subscribed!</h3>
					<p className="text-xs text-muted-foreground text-center">
						Thanks for supporting EarthBrowser. Full forecasts, the 48-hour time
						machine, and 5 devices are now unlocked. A receipt is on its way to
						your email.
					</p>
					<Button
						onClick={onDismiss}
						className="rounded-full shadow-lg transition-all duration-300 hover:scale-105 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
					>
						Start exploring
					</Button>
				</>
			) : (
				<>
					<div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
					<p className="text-xs text-muted-foreground text-center">
						Payment received — activating your subscription…
					</p>
				</>
			)}
		</div>
	)
}

function CheckoutCancelled({ onDismiss }: { onDismiss: () => void }) {
	const dispatch = useAppDispatch()

	const tryAgain = () => {
		onDismiss()
		dispatch(setSubscribeOpen(true))
	}

	return (
		<div className="flex flex-col items-center gap-4 px-6 py-6">
			<h3 className="text-base font-bold text-foreground">Checkout cancelled</h3>
			<p className="text-xs text-muted-foreground text-center">
				You have not been charged.
			</p>
			<div className="flex items-center gap-3">
				<Button
					onClick={tryAgain}
					className="rounded-full shadow-lg transition-all duration-300 hover:scale-105 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
				>
					Try again
				</Button>
				<Button variant="ghost" onClick={onDismiss} className="rounded-full text-muted-foreground">
					<XIcon className="h-4 w-4 mr-1" />
					Not now
				</Button>
			</div>
		</div>
	)
}
